import React, { FC } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useAppSelector } from "hooks/useReduxStore";
import menusInWeekSelector from "store/selectors/menu/menusInWeekSelector";
import ColoredCircularProgress from "components/common/ui/ColorCircularProgress";
import { css } from "styled-components";
import { Menu } from "client/jspPlatformExperiment";
import dayjs from "dayjs";
import MenuCell from "./MenuCell";

const weekdays = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

enum mealTypes {
  LUNCH = "lunch",
  DINNER = "dinner",
}

const organizeMenus = (
  menus: Menu[],
): { [key: string]: { lunch: Menu[]; dinner: Menu[] } } => {
  // Organize menus by their serving_datetime, grouping by day key in `days` array.
  // Assume `weekdays` is an array of day keys (e.g., ["Mon", "Tue", ...]) defined at the top of the file.
  // Each menu has a `serving_datetime` property (string or Date).
  // Return an object: { [dayKey]: Menu[] }
  const dayMenus: { [key: string]: { lunch: Menu[]; dinner: Menu[] } } = {};
  weekdays.forEach((weekday) => {
    dayMenus[weekday] = { lunch: [], dinner: [] };
  });

  menus.forEach((menu) => {
    // Parse the serving_datetime to a Date object
    const menuServingDatetime = dayjs(menu.serving_datetime);
    // Get the day of week as index (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    // Adjust if your `days` array starts with Monday (1) or Sunday (0)
    // Here, assume days[0] is Sunday, days[1] is Monday, etc.
    const dayIdx = menuServingDatetime.toDate().getDay();
    const dayKey = weekdays[dayIdx];
    const mealType =
      menuServingDatetime.toDate().getHours() < 14
        ? mealTypes.LUNCH
        : mealTypes.DINNER;
    if (dayKey) {
      dayMenus[dayKey][mealType].push(menu);
    }
  });

  return dayMenus;
};

const WeekCalendarMenuView: FC = () => {
  const menus = useAppSelector(menusInWeekSelector.menusInWeek);
  const startDate = useAppSelector(menusInWeekSelector.menusInWeekStartDate);
  const isLoading = useAppSelector(menusInWeekSelector.isMenusInWeekLoading);

  return (
    <Box>
      {isLoading ? (
        <ColoredCircularProgress customColor={css`rgb(120, 203, 255)`} />
      ) : (
        <Grid container spacing={0} alignItems="stretch">
          {Object.entries(organizeMenus(menus ?? [])).map(
            ([dayKey, dayMenus], idx) => (
              <Grid item xs={12} sm={6} md={3} lg={1.71} key={dayKey}>
                {startDate && idx < startDate.toDate().getDay() ? (
                  <Box sx={{ mb: 1, width: "100%" }}>
                    <Typography
                      align="center"
                      variant="subtitle2"
                      sx={{ width: "100%" }}
                    >
                      •
                    </Typography>
                  </Box>
                ) : null}
                <Box sx={{ mb: 1, width: "100%" }}>
                  <Typography
                    align="center"
                    variant="subtitle2"
                    sx={{ width: "100%" }}
                  >
                    {dayKey}
                  </Typography>
                </Box>
                {startDate && idx >= startDate.toDate().getDay() ? (
                  <Box sx={{ mb: 1, width: "100%" }}>
                    <Typography
                      align="center"
                      variant="subtitle2"
                      sx={{ width: "100%" }}
                    >
                      •
                    </Typography>
                  </Box>
                ) : null}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0,
                  }}
                >
                  {dayMenus.lunch.length > 0 ? (
                    <MenuCell mealType="lunch" menuData={dayMenus.lunch[0]} />
                  ) : null}
                  {dayMenus.dinner.length > 0 ? (
                    <MenuCell mealType="dinner" menuData={dayMenus.dinner[0]} />
                  ) : null}
                </Box>
              </Grid>
            ),
          )}
        </Grid>
      )}
    </Box>
  );
};

export default WeekCalendarMenuView;
